<template>
  <VDialogV3
      id="payment-request-card"
      position="bottom"

      :entity="localPaymentRequest"

      show-edit-button
      :edit-button-disabled="localPaymentRequest?.editDisabledReason"
      :submit-method="submit"
      :errors="dialogErrors"
      :context-menu="PaymentRequestsService.contextMenu"
      :on-context-menu-action="onContextMenuAction"

      @edit-button:clicked="showRequisitesDialog"

      width="1650px"
  >
    <template #header>
      <template v-if="localPaymentRequest">
        <div class="title">
          {{ localPaymentRequest.toString() }}

          <span
              v-if="localPaymentRequest.externalNumber"
              class="subtitle external-number"
          >
            Внешний №{{ localPaymentRequest.externalNumber }}
            <VLink
                @click="$core.copyToClipboard(localPaymentRequest!.externalNumber ?? '')"
                material="content_copy"
            />
          </span>
        </div>

        <VBadges>
          <VBadge v-if="localPaymentRequest.isCanceled === true" class="warning">Отменён</VBadge>
          <VBadge v-if="localPaymentRequest.isOfferBased === false" class="primary">Прямой</VBadge>
        </VBadges>

        <div class="payment-info">
          <div class="payment-sync">
            <div>Синхронизация:</div>
            <template v-if="localPaymentRequest.isExisting === true">
              <PaymentRequestExportStatus :payment-request="localPaymentRequest" />
            </template>
            <template v-else>
              --
            </template>
          </div>
          <div class="payment-status ellipsis">
            <div>Статус:</div>
            <div :class="{ 'error': localPaymentRequest.isCanceled }">
              {{ localPaymentRequest.statusText }}
            </div>
          </div>
        </div>
      </template>
    </template>

    <header class="document-header">
      <dl>
        <dt>Получатель</dt>
        <dd
            class="ellipsis"
            :title="localPaymentRequest.receiverCompany ? localPaymentRequest.receiverCompany.name : 'Не задано'"
        >
          {{ localPaymentRequest.receiverCompany ? localPaymentRequest.receiverCompany.name : 'Не задано' }}
        </dd>
        <dt>Расчётный счёт</dt>
        <dd
            class="ellipsis"
            :title="localPaymentRequest.receiverBankAccount?.toString() ?? 'Не выбрано'"
        >
          {{ localPaymentRequest.receiverBankAccount?.toString() ?? 'Не выбрано' }}
        </dd>

        <hr>

        <dt>Назначение</dt>
        <dd
            class="ellipsis"
            :title="localPaymentRequest.reasonText"
        >
          {{ localPaymentRequest.reasonText }}
        </dd>
        <dt>Приоритет</dt>
        <dd class="ellipsis">
          <template v-if="localPaymentRequest.priorityType">
            {{ localPaymentRequest.priorityType.fullName }}
          </template>
          <template v-else>
            --
          </template>
        </dd>
      </dl>

      <dl>
        <dt>Плательщик</dt>
        <dd
            class="ellipsis"
            :title="localPaymentRequest.payer?.name || '--'"
        >
          {{ localPaymentRequest.payer?.name || '--' }}
        </dd>
        <dt>Ответственный</dt>
        <dd
            class="ellipsis"
            :title="localPaymentRequest.responsible?.name ?? '--'"
        >
          {{ localPaymentRequest.responsible?.name ?? '--' }}
        </dd>

        <hr>

        <dt>Валюта сделки</dt>
        <dd class="ellipsis">
          {{ localPaymentRequest.orderCurrency?.description ?? '--' }}
        </dd>
        <dt>Комментарий</dt>
        <dd class="ellipsis">
          {{ localPaymentRequest.commentary || 'Не указан' }}
        </dd>
      </dl>

      <aside class="totals">
        <section>
          <header>План</header>

          <dl>
            <dt class="total">Итого</dt>
            <dd class="total" v-if="!localPaymentRequest.paymentRequestSumFull.isEmpty()">
              {{ localPaymentRequest.paymentRequestSumFull.format(localPaymentRequest.paymentRequestCurrencyCode) }}
            </dd>
            <dd class="total" v-else>--</dd>

            <dt>НДС в т.ч.</dt>
            <dd v-if="localPaymentRequest.hasVat && !localPaymentRequest.paymentRequestVatSum.isEmpty()">
              {{ localPaymentRequest.paymentRequestVatSum.format(localPaymentRequest.paymentRequestCurrencyCode) }}
            </dd>
            <dd v-else>Не облагается</dd>

            <dt>Курс платежа</dt>
            <dd v-if="localPaymentRequest.currencyRateText">
              {{ localPaymentRequest.currencyRateText }}
            </dd>
            <dd v-else>--</dd>

            <dt>Дата оплаты</dt>
            <dd>{{ $core.utils.date.format(localPaymentRequest.plannedPaymentDate) }}</dd>
          </dl>
        </section>

        <section>
          <header>Факт</header>

          <dl>
            <dt class="total">Итого</dt>
            <dd class="total" v-if="localPaymentRequest.actualPaymentSumFull !== null">
              {{ localPaymentRequest.actualPaymentSumFull.format(localPaymentRequest.actualPaymentCurrencyCode) }}
            </dd>
            <dd class="total" v-else>--</dd>

            <dt>НДС в т.ч.</dt>
            <dd v-if="localPaymentRequest.hasVat && localPaymentRequest.actualPaymentVatSum !== null">
              {{ localPaymentRequest.actualPaymentVatSum.format(localPaymentRequest.actualPaymentCurrencyCode) }}
            </dd>
            <dd v-else-if="localPaymentRequest.hasVat">--</dd>
            <dd v-else>Не облагается</dd>

            <dt>Курс платежа</dt>
            <dd v-if="localPaymentRequest.actualPaymentCurrencyRate !== null">
              {{ localPaymentRequest.actualPaymentCurrencyRate.format() }}
              {{ localPaymentRequest.actualPaymentCurrency?.symbol ?? '' }}
            </dd>
            <dd v-else>--</dd>

            <dt>Дата оплаты</dt>
            <dd v-if="localPaymentRequest.actualPaymentDate">
              {{ $core.utils.date.format(localPaymentRequest.actualPaymentDate) }}
            </dd>
            <dd v-else>--</dd>
          </dl>
        </section>
      </aside>
    </header>

    <VDataGridV2
        :data="localPaymentRequest.positions"
        class="fill"
    >
      <VDataGridColumnV2
          id="buyersCode"
          label="Арт"
          content-align="center"
          width="90px"
      >
        <template #label>
          <div>Арт</div>
          <div class="description">№</div>
        </template>
        <template #default="{ row }">
          <VLink
              class="ellipsis"
              @click="OrdersService.showCodeInfoPopup(row.orderPosition, $event.currentTarget)"
          >
            {{ row.orderPosition.buyersCode || '--' }}
          </VLink>
          <div class="description nowrap">
            {{ $core.utils.string.formatWithDelimiter(row.orderPosition.id) }}
          </div>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
          id="name"
      >
        <template #label>
          Товар
          <div class="description">Производитель / материал</div>
        </template>
        <template #default="{ row }">
            <span
                class="ellipsis"
                :title="row.orderPosition.name"
            >
              {{ row.orderPosition.name }}
            </span>

          <div class="description">
            <template v-if="row.orderPosition.material">
              Материал: {{ row.orderPosition.material }}
            </template>
            <template v-if="row.orderPosition.manufacturer">
              Производитель: {{ row.orderPosition.manufacturer.name }}
            </template>
          </div>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
          id="plannedPaymentDate"
          width="120px"
          content-align="left"
          label="Плановая дата оплаты"
          header-class="nowrap"
      >
        <template #default="{ row }">
          {{ row.orderPosition.plannedPaymentDate.format() || '--' }}

          <VLink class="order-position-info" @click="OrdersService.showPositionPaymentTermsPopup(row.orderPosition, $event.currentTarget)" material="info"></VLink>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
          id="qty"
          content-align="right"
          width="130px"
          label="Кол-во"
          header-class="nowrap"
      >
        <template #default="{ row, index }">
          <div class="main">
            <VInputNumberRusV2
                :key="'qty' + index"
                autocomplete="off"
                :type="Qty"
                :disabled="localPaymentRequest.isEditable === false"
                :title="localPaymentRequest.editDisabledReason"
                :min="0.001"
                :model-value="row.qty"
                @update:modelValue="value => (row.qty = value)"

                :unit="row.unit?.name ?? ''"
            />
          </div>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
          id="price-full"
          content-align="right"
          width="150px"
          header-class="nowrap"
      >
        <template #label>
          Цена {{ localPaymentRequest.hasVat ? 'с' : 'без' }} НДС
        </template>
        <template #default="{ row, index }">
          <div class="main">
            <VInputNumberRusV2
                :key="'priceFull' + index"
                class="price-input"
                disabled
                :type="Price"
                :model-value="row.paymentRequestPriceFull"

                :unit="localPaymentRequest.paymentRequestCurrency?.symbol ?? ''"
            >
              <template #helper-text>
                <div v-if="localPaymentRequest.hasCurrencyDifference">
                  {{ row.priceFullSeller.format(localPaymentRequest.orderCurrency?.code ?? null) }}
                </div>
              </template>
            </VInputNumberRusV2>
          </div>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
          id="sum-full"
          content-align="right"
          width="150px"
          label="Сумма полная"
          header-class="nowrap"
      >
        <template #default="{ row, index }">
          <div class="main">
            <VInputNumberRusV2
                :ref="'sumInput' + index"
                :key="'sumInput' + index"
                class="sum-input"
                autocomplete="off"
                :type="Sum"
                :class="{ 'touched-input': row.paymentRequestSumFull !== null }"
                :disabled="localPaymentRequest.reasonUnavailableEditPositionSum !== null"
                :title="localPaymentRequest.reasonUnavailableEditPositionSum"
                :min="0.001"
                :validate-method="value => validatePaymentRequestSumFull(value, row)"
                :model-value="row.paymentRequestSumFull ?? row.paymentRequestSumFullCalculated"
                @update:modelValue="value => (row.paymentRequestSumFull = value)"

                :unit="localPaymentRequest?.paymentRequestCurrency?.symbol ?? ''"
            >
              <template #helper-text>
                <div
                    v-if="row.hasSumDeviation"
                    class="error"
                >
                  {{ `Отклонение от ${row.paymentRequestSumFullCalculated.format()} более чем на 1% ` }}
                </div>

                <div v-if="localPaymentRequest.hasCurrencyDifference">
                  {{ row.sumFullSeller.format(localPaymentRequest.orderCurrency?.code ?? null) }}
                </div>
              </template>
            </VInputNumberRusV2>
          </div>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
          id="paymentStatus"
          width="200px"
          label="Статус оплаты"
          header-class="nowrap"
      >
        <template #default="{ row }">
          <PaymentStatusBar
              :payment-status="row.orderPosition.paymentStatus"
              :currency="localPaymentRequest.orderCurrencyCode"
              :current="paymentStatusCurrent(row)"
              :is-current-paid="localPaymentRequest.isPaid"
              @open="(anchor) => OrdersService.showPositionPaymentsPopup(row.orderPosition, anchor)"
          />
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
          id="shipmentStatus"
          width="200px"
          label="Статус отгрузки"
          header-class="nowrap"
      >
        <template #default="{ row }">
          <ShipmentStatusBar
              :shipment-status="row.orderPosition.shipmentStatus"
              :unit="row.orderPosition.unit"
              @open="(anchor) => OrdersService.showPositionShipmentsPopup(row.orderPosition, anchor)"
          />
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
          id="actions"
          width="70px"
          content-align="right"
      >
        <template #default="{ row }">
          <VButton
              material="delete"
              :disabled="localPaymentRequest.isEditable === false"
              :title="localPaymentRequest.editDisabledReason"
              @click="localPaymentRequest.deletePosition(row)"
          />
        </template>
      </VDataGridColumnV2>

      <VDataGridActionBarV2
          v-if="localPaymentRequest.isExisting === false"
          class="centered"
          :class="{ empty: localPaymentRequest.positions.length === 0 }"
      >
        <VLink
            icon-postfix
            :disabled="localPaymentRequest.reasonUnavailableAddPositions !== null"
            :title="localPaymentRequest.reasonUnavailableAddPositions"
            @click="openPositionsDialog"
            material="add"
        >
          Добавить товар
        </VLink>
      </VDataGridActionBarV2>

    </VDataGridV2>
  </VDialogV3>
</template>

<style lang="scss" src="./PaymentRequestCard.scss" />

<script lang="ts">

import PaymentRequestExportStatus           from '@/domains/PaymentRequests/components/PaymentRequestsRegistry/PaymentRequestExportStatus.vue';
import PaymentStatusBar                     from '@/domains/Orders/components/PaymentStatusBar/PaymentStatusBar.vue';
import ShipmentStatusBar                    from '@/domains/Orders/components/ShipmentStatusBar/ShipmentStatusBar.vue';

import PaymentRequest                       from '@/domains/PaymentRequests/PaymentRequest';
import PaymentRequestPosition               from '@/domains/PaymentRequests/PaymentRequestPosition'
import PaymentRequestsService               from '@/domains/PaymentRequests/PaymentRequestsService';
import OrdersService                        from '@/domains/Orders/OrdersService';
import OrderPosition                        from "@/domains/Orders/OrderPosition";
import Shipment                             from '@/domains/Shipments/Shipment';
import ShipmentsService                     from '@/domains/Shipments/ShipmentsService';
import PartnersService                      from '@/domains/Partners/PartnersService';
import Price                                from "@/core/types/Price"
import Sum                                  from "@/core/types/Sum"
import Qty                                  from "@/core/types/Qty";

export default defineComponent({
  name: 'PaymentRequestCard',

  components: {
    PaymentRequestExportStatus,
    PaymentStatusBar,
    ShipmentStatusBar,
  },

  props: {
    /** Готовый объект платежа — передаётся из реестра */
    paymentRequest: {
      type: PaymentRequest,
      default: null,
    },
    /** ID платежа — передаётся из роутера, карточка сама загружает объект */
    id: {
      type: Number,
      default: null,
    },
    /**
     * Отгрузка (объект или id) для создания платежа на её основе.
     * Карточка догрузит отгрузку и получателя и соберёт черновик через fromShipment.
     */
    shipmentOrId: {
      type:    [Shipment, Number] as PropType<Shipment | number | null>,
      default: null,
    },
    /**
     * Позиция заказа (объект или id) для создания платежа на её основе.
     * Карточка догрузит позицию и получателя и соберёт черновик через
     * fromOrderPosition. Взаимоисключается с shipmentOrId: основание одно.
     */
    orderPositionOrId: {
      type:    [OrderPosition, Number] as PropType<OrderPosition | number | null>,
      default: null,
    },
  },

  setup() {
    return {
      Price,
      Sum,
      Qty,
      PaymentRequestsService,
      OrdersService,
    }
  },

  data() {
    return {
      localPaymentRequest: null as PaymentRequest | null,
    };
  },

  async mounted() {
    await this.init();

    if (this.localPaymentRequest.isExisting === false) {
      this.showRequisitesDialog();
    }
  },

  computed: {
    /**
     * Массив ошибок для плашки над футером VDialogV3 — та же причина, по
     * которой заблокирована кнопка. Раньше условий блокировки было три, а
     * объяснялось одно, и в остальных случаях кнопка гасла без причины.
     */
    dialogErrors() {
      if (this.$core.utils.isNullOrUndefined(this.localPaymentRequest) === true) {
        return [];
      }
      else if (this.localPaymentRequest.reasonUnavailableSubmit === null) {
        return [];
      }
      else {
        return [{ message: this.localPaymentRequest.reasonUnavailableSubmit }];
      }
    },
  },

  methods: {
    // ==================== Инициализация ====================

    async init(): Promise<void> {
      if (this.id || this.paymentRequest) {
        // Открываем существующий: объект из реестра либо id (роутер/диалоговая система)
        if (this.paymentRequest instanceof PaymentRequest) {
          this.localPaymentRequest = this.$core.utils.cloneDeep(this.paymentRequest);
        } else {
          this.localPaymentRequest = await PaymentRequestsService.get(this.id);
        }
      }
      else {
        // Создаём новый: на основе отгрузки, позиции заказа либо с нуля
        if (this.shipmentOrId) {
          this.localPaymentRequest = await this.buildFromShipment(this.shipmentOrId);
        } else if (this.orderPositionOrId) {
          this.localPaymentRequest = await this.buildFromOrderPosition(this.orderPositionOrId);
        } else {
          this.localPaymentRequest = PaymentRequest.fromNew();
        }
      }
    },

    /**
     * Собирает черновик платежа из отгрузки.
     * Догружает полную отгрузку (если передан id) и резолвит получателя
     * с его компанией — fromShipment ждёт receiver/receiverCompany для реквизитов.
     */
    async buildFromShipment(shipmentOrId: Shipment | number): Promise<PaymentRequest> {
      let shipment: Shipment | null;

      if (shipmentOrId instanceof Shipment) {
        shipment = shipmentOrId;
      }
      else {
        shipment = await ShipmentsService.get(shipmentOrId);
      }

      const receiver = await PartnersService.get(shipment.seller.id);
      const receiverCompany = receiver?.companies.find(
          company => company.id === shipment.sellerCompany.id,
      );

      return PaymentRequest.fromShipment(shipment, { receiver, receiverCompany });
    },

    /**
     * Собираем платеж из позиции заказа.
     * Догружает полную позицию (если передан id) и резолвит получателя
     * с его компанией — fromOrderPosition ждёт receiver/receiverCompany
     * для реквизитов и для проверки НДС при добавлении позиции.
     */
    async buildFromOrderPosition(orderPositionOrId: OrderPosition | number): Promise<PaymentRequest> {
      let orderPosition: OrderPosition | null;

      if (orderPositionOrId instanceof OrderPosition) {
        orderPosition = orderPositionOrId;
      }
      else {
        orderPosition = await OrdersService.getPosition(orderPositionOrId, false, this.$core.currentUser.isAdmin);
      }

      const receiver = await PartnersService.get(orderPosition.seller.id);
      const receiverCompany = receiver?.companies.find(
          company => company.id === orderPosition.sellerCompany.id,
      );

      return PaymentRequest.fromOrderPosition(orderPosition, { receiver, receiverCompany });
    },

    // ==================== Позиции ====================

    /**
     * Доля позиции в прогресс-баре оплаты — её сумма в валюте заказа, либо
     * null, если платёж в агрегатах статуса не участвует. Отменённый платёж
     * сервер в payed и inQueue не считает, выделять в баре нечего.
     */
    paymentStatusCurrent(position: PaymentRequestPosition): Sum | null {
      if (this.localPaymentRequest!.isCanceled === true) {
        return null;
      }
      else {
        return position.sumFullSeller;
      }
    },

    // ==================== Валидация ====================

    // hasSumDeviation переехал в PaymentRequestPosition: сравнение ввода с
    // расчётом - свойство позиции, а не карточки, и оно нужно не только здесь.
    // Заодно ушёл спуск в toNumber: модель сравнивает в Decimal.

    validatePaymentRequestSumFull(value: Sum | null, position: PaymentRequestPosition): string | null {
      if (this.localPaymentRequest!.isExisting === true) return null;

      const basePosition = this.localPaymentRequest!.positions.find(
          (item: PaymentRequestPosition) => item.orderPosition.paymentStatus.isFullyClassified === false,
      );

      if (position === basePosition) return null;
      if (value === null || value.isEmpty()) return null;

      const maxValue = position.orderPosition.paymentStatus.maxAvailableCurrent
          .mul(this.localPaymentRequest!.paymentRequestCurrencyRate);

      if (
          value.greaterThan(maxValue)
          && !this.$core.utils.number.isCloseTo(value.toNumber(), maxValue.toNumber())
      ) {
        return `Отклонение от ${maxValue.format()} ${
            this.localPaymentRequest!.paymentRequestCurrency?.symbol ?? ''
        } более чем на 1%`;
      }
      return null;
    },

    // ==================== Диалоги ====================

    showRequisitesDialog() {
      this.$core.dialogs.PaymentRequestRequisites.show(
          {
            paymentRequest: this.localPaymentRequest,
          },
          {
            save: (updatedPaymentRequest: PaymentRequest) => {
              this.localPaymentRequest = updatedPaymentRequest;
            },
            close: () => {
              if (this.localPaymentRequest.isExisting === false) {
                this.$core.dialogs.PaymentRequestCard.hide();
              }
            },
          },
      );
    },

    openPositionsDialog() {
      this.$core.dialogs.PaymentRequestPositionsDialog.show({
        paymentRequest: this.localPaymentRequest,
        partnerId:      this.localPaymentRequest!.receiver?.id,
        companyId:      this.localPaymentRequest!.receiverCompany?.id,
      });
    },

    // ==================== Сабмит ====================

    async submit(): Promise<boolean> {
      if (this.localPaymentRequest!.isExisting === true) {
        return await PaymentRequestsService.update(this.localPaymentRequest!);
      }
      else {
        return await PaymentRequestsService.create(this.localPaymentRequest!);
      }
    },

    /** Пункт меню что-то изменил (например, отмена) — перечитываем платёж */
    async onContextMenuAction(): Promise<void> {
      this.localPaymentRequest = await PaymentRequestsService.get(this.localPaymentRequest!.id);
    },
  },
});
</script>
